// frontend/src/samkalp-admin/SamkalpAdminDashboard.js
//
// Unified Admin Dashboard shell: composes existing Studio components
// (Projects, Users, Content Standards, Integrations, Analytics, Branding,
// Audit) alongside duplicated AskOSAdmin-equivalent tabs (Students, Project
// Access, Course Mapping, Quotas, Current Affairs) — one login, one
// dashboard, scoped to whichever tenant (Samkalp itself, or one centre)
// the admin belongs to. Zero changes to Studio/AskOSAdmin themselves.
import React from 'react';
import { Routes, Route, NavLink } from 'react-router-dom';
import DashboardHome from './DashboardHome';
import ProjectsPanel from './panels/ProjectsPanel';
import UsersPanel from './panels/UsersPanel';
import ContentStandardsPanel from './panels/ContentStandardsPanel';
import IntegrationsPanel from './panels/IntegrationsPanel';
import AnalyticsPanel from './panels/AnalyticsPanel';
import BrandingPanel from './panels/BrandingPanel';
import AuditPanel from './panels/AuditPanel';
import StudentsTab from './tabs/StudentsTab';
import ProjectAccessTab from './tabs/ProjectAccessTab';
import CourseMappingTab from './tabs/CourseMappingTab';
import QuotaTab from './tabs/QuotaTab';
import CurrentAffairsTab from './tabs/CurrentAffairsTab';
import PyqBankTab from './tabs/PyqBankTab';
import ContentBlocksTab from './tabs/ContentBlocksTab';
import MockTestsTab from './tabs/MockTestsTab';
import './SamkalpAdminDashboard.css';

const NAV_GROUPS = [
    {
        heading: 'Overview',
        items: [{ key: '', label: 'Dashboard' }],
    },
    {
        heading: 'Studio (Curriculum & Content)',
        items: [
            { key: 'projects', label: 'Projects' },
            { key: 'users', label: 'Users & Faculty' },
            { key: 'content-standards', label: 'Content Standards' },
            { key: 'integrations', label: 'Integrations' },
            { key: 'analytics', label: 'Subscription Analytics' },
            { key: 'branding', label: 'Branding' },
            { key: 'audit', label: 'Audit Logs' },
        ],
    },
    {
        heading: 'AskOS (Student Delivery)',
        items: [
            { key: 'students', label: 'Students' },
            { key: 'project-access', label: 'Project Access' },
            { key: 'course-mapping', label: 'Course Mapping' },
            { key: 'quotas', label: 'Credit Quotas' },
            { key: 'current-affairs', label: 'Current Affairs' },
            { key: 'pyq-bank', label: 'PYQ Bank' },
            { key: 'content-blocks', label: 'Content Blocks' },
            { key: 'mock-tests', label: 'Mock Tests' },
        ],
    },
];

const SamkalpAdminDashboard = ({ slug, onLogout }) => (
    <div className="samkalp-admin-shell">
        <aside className="samkalp-admin-sidebar">
            <div className="samkalp-admin-brand">
                <div className="samkalp-admin-brand-sun">☀</div>
                <div>
                    <strong>SAMKALP</strong>
                    <span>Admin</span>
                </div>
            </div>

            <nav className="samkalp-admin-nav">
                {NAV_GROUPS.map(group => (
                    <div key={group.heading} className="samkalp-admin-nav-group">
                        <div className="samkalp-admin-nav-heading">{group.heading}</div>
                        {group.items.map(item => (
                            <NavLink
                                key={item.key}
                                to={item.key ? `/samkalp-ias/admin/${item.key}` : '/samkalp-ias/admin'}
                                end={item.key === ''}
                                className={({ isActive }) => `samkalp-admin-nav-link ${isActive ? 'active' : ''}`}
                            >
                                {item.label}
                            </NavLink>
                        ))}
                    </div>
                ))}
            </nav>

            <div className="samkalp-admin-sidebar-footer">
                <div className="samkalp-admin-slug">{slug}</div>
                <button className="samkalp-admin-logout-btn" onClick={onLogout}>Log out</button>
            </div>
        </aside>

        <main className="samkalp-admin-main">
            <Routes>
                <Route index element={<DashboardHome slug={slug} />} />
                <Route path="projects" element={<ProjectsPanel slug={slug} />} />
                <Route path="users" element={<UsersPanel slug={slug} />} />
                <Route path="content-standards" element={<ContentStandardsPanel slug={slug} />} />
                <Route path="integrations" element={<IntegrationsPanel slug={slug} />} />
                <Route path="analytics" element={<AnalyticsPanel slug={slug} />} />
                <Route path="branding" element={<BrandingPanel slug={slug} />} />
                <Route path="audit" element={<AuditPanel slug={slug} />} />
                <Route path="students" element={<StudentsTab slug={slug} />} />
                <Route path="project-access" element={<ProjectAccessTab slug={slug} />} />
                <Route path="course-mapping" element={<CourseMappingTab slug={slug} />} />
                <Route path="quotas" element={<QuotaTab slug={slug} />} />
                <Route path="current-affairs" element={<CurrentAffairsTab slug={slug} />} />
                <Route path="pyq-bank" element={<PyqBankTab slug={slug} />} />
                <Route path="content-blocks" element={<ContentBlocksTab slug={slug} />} />
                <Route path="mock-tests" element={<MockTestsTab slug={slug} />} />
            </Routes>
        </main>
    </div>
);

export default SamkalpAdminDashboard;
